import type { z } from 'zod';

import { API_PREFIX, DEVICE_HEADER } from './config';
import { AkpError, ErrorCodes } from './errors';
import {
  buildPath,
  buildQuery,
  type PathParamNames,
  type PathParams,
  type RouteDef,
  type RouteKey,
  type Routes,
  routes,
} from './routes';

/**
 * Framework-agnostic typed API client (docs/10 WP0.3). Works in Workers,
 * browsers (Chromium ≥ 80) and Node ≥ 20 — only `fetch`, `AbortController`,
 * `URLSearchParams` and `setTimeout` are used.
 */

type MaybePromise<T> = T | Promise<T>;

export interface ApiClientOptions {
  /** Origin of the API, e.g. `https://api.acapella.lol` or `http://localhost:8787`. */
  baseUrl: string;
  /** Path prefix, default `/v1`. Pass `''` when `baseUrl` already includes it. */
  prefix?: string;
  /** Firebase ID token (user or TV). Return null/undefined for unauthenticated calls. */
  getToken?: () => MaybePromise<string | null | undefined>;
  /** When provided, every request carries `X-Akapella-Device` (docs/07 §3). */
  getDeviceId?: () => MaybePromise<string | null | undefined>;
  /** Extra static headers (e.g. `Accept-Language`). */
  headers?: Record<string, string> | (() => Record<string, string>);
  fetch?: typeof fetch;
  /** Per-attempt timeout, default 15 000 ms. */
  timeoutMs?: number;
  /** Retries for GET requests on network errors / 429 / 5xx, default 3. */
  retries?: number;
  /** Base of the exponential backoff (ms), default 300. Set 0 in tests. */
  retryBaseMs?: number;
  /** Parse responses with the route's zod schema (default true; disable in prod). */
  validateResponses?: boolean;
  /** Observability hook: called once per failed call (after retries). */
  onError?: (error: AkpError, info: { route: RouteKey; attempt: number }) => void;
}

export interface CallOptions {
  signal?: AbortSignal;
  headers?: Record<string, string>;
  timeoutMs?: number;
  /** Overrides the client default for this GET call. */
  retries?: number;
}

type ParamsArg<R> = R extends { path: infer P extends string }
  ? [PathParamNames<P>] extends [never]
    ? object
    : { params: PathParams<P> }
  : object;

type QueryArg<R> = R extends { query: infer Q extends z.ZodTypeAny }
  ? object extends z.input<Q>
    ? { query?: z.input<Q> }
    : { query: z.input<Q> }
  : object;

type BodyArg<R> = R extends { bodyKind: 'form' }
  ? { body: FormData }
  : R extends { bodyKind: 'binary' }
    ? { body: ArrayBuffer | ArrayBufferView }
    : R extends { body: infer B extends z.ZodTypeAny }
      ? { body: z.input<B> }
      : object;

/** Arguments accepted by the method generated for a route. */
export type RouteArgs<R> = ParamsArg<R> & QueryArg<R> & BodyArg<R> & CallOptions;

/** Value resolved by the method generated for a route. */
export type RouteResult<R> = R extends { responseKind: 'binary' }
  ? ArrayBuffer
  : R extends { responseKind: 'empty' }
    ? void
    : R extends { response: infer S extends z.ZodTypeAny }
      ? z.infer<S>
      : never;

type RequiredKeys<T> = {
  [K in keyof T]-?: object extends Pick<T, K> ? never : K;
}[keyof T];

type Method<R> = [RequiredKeys<Omit<RouteArgs<R>, keyof CallOptions>>] extends [never]
  ? (args?: RouteArgs<R>) => Promise<RouteResult<R>>
  : (args: RouteArgs<R>) => Promise<RouteResult<R>>;

export type ApiClient = { readonly [K in RouteKey]: Method<Routes[K]> } & {
  /** Generic entry point, same typing as the per-route methods. */
  request<K extends RouteKey>(
    route: K,
    args?: RouteArgs<Routes[K]>,
  ): Promise<RouteResult<Routes[K]>>;
  /** Absolute URL for a route (e.g. to put a GET in an `<img src>`). */
  url<K extends RouteKey>(route: K, args?: UrlArgs<Routes[K]>): string;
};

export type UrlArgs<R> = ParamsArg<R> & QueryArg<R>;

const DEFAULTS = {
  timeoutMs: 15_000,
  retries: 3,
  retryBaseMs: 300,
} as const;

function isRetryableStatus(status: number): boolean {
  return status === 429 || status >= 500;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function backoffMs(base: number, attempt: number): number {
  if (base <= 0) return 0;
  const exp = base * 2 ** (attempt - 1);
  return exp + Math.floor(Math.random() * Math.min(100, exp));
}

async function readBody(res: Response, binary: boolean): Promise<unknown> {
  if (binary && res.ok) return res.arrayBuffer();
  if (res.status === 204) return undefined;
  const text = await res.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  const base = options.baseUrl.replace(/\/+$/, '');
  const prefix = options.prefix ?? API_PREFIX;
  const fetchImpl = options.fetch ?? globalThis.fetch;
  const validate = options.validateResponses ?? true;

  function url(def: RouteDef, args?: { params?: Record<string, string>; query?: object }): string {
    return `${base}${prefix}${buildPath(def.path, args?.params)}${buildQuery(
      args?.query as Record<string, unknown> | undefined,
    )}`;
  }

  async function headersFor(def: RouteDef, extra?: Record<string, string>): Promise<Headers> {
    const h = new Headers();
    h.set('Accept', def.responseKind === 'binary' ? '*/*' : 'application/json');
    const staticHeaders =
      typeof options.headers === 'function' ? options.headers() : options.headers;
    for (const [k, v] of Object.entries(staticHeaders ?? {})) h.set(k, v);
    const token = options.getToken ? await options.getToken() : null;
    if (token) h.set('Authorization', `Bearer ${token}`);
    const deviceId = options.getDeviceId ? await options.getDeviceId() : null;
    if (deviceId) h.set(DEVICE_HEADER, deviceId);
    for (const [k, v] of Object.entries(extra ?? {})) h.set(k, v);
    return h;
  }

  function encodeBody(def: RouteDef, body: unknown, headers: Headers): BodyInit | undefined {
    if (body === undefined) return undefined;
    if (def.bodyKind === 'form') return body as FormData;
    if (def.bodyKind === 'binary') {
      if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/octet-stream');
      return body as BodyInit;
    }
    headers.set('Content-Type', 'application/json');
    return JSON.stringify(body);
  }

  async function attempt(
    def: RouteDef,
    target: string,
    headers: Headers,
    body: BodyInit | undefined,
    timeoutMs: number,
    signal: AbortSignal | undefined,
  ): Promise<Response> {
    if (signal?.aborted) {
      throw new AkpError(ErrorCodes.network_error, 'Request aborted', { status: 0 });
    }
    const ctrl = new AbortController();
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      ctrl.abort();
    }, timeoutMs);
    const onAbort = () => ctrl.abort();
    signal?.addEventListener('abort', onAbort, { once: true });
    try {
      return await fetchImpl(target, { method: def.method, headers, body, signal: ctrl.signal });
    } catch (cause) {
      if (timedOut) {
        throw new AkpError(ErrorCodes.timeout, `Request timed out after ${timeoutMs} ms`, {
          status: 0,
          cause,
        });
      }
      if (signal?.aborted) {
        throw new AkpError(ErrorCodes.network_error, 'Request aborted', { status: 0, cause });
      }
      throw new AkpError(ErrorCodes.network_error, 'Network error', { status: 0, cause });
    } finally {
      clearTimeout(timer);
      signal?.removeEventListener('abort', onAbort);
    }
  }

  async function request(key: RouteKey, rawArgs?: unknown): Promise<unknown> {
    const def: RouteDef = routes[key];
    const args = (rawArgs ?? {}) as {
      params?: Record<string, string>;
      query?: object;
      body?: unknown;
    } & CallOptions;
    const target = url(def, args);
    const timeoutMs = args.timeoutMs ?? options.timeoutMs ?? DEFAULTS.timeoutMs;
    const retries =
      def.method === 'GET' ? (args.retries ?? options.retries ?? DEFAULTS.retries) : 0;
    const retryBase = options.retryBaseMs ?? DEFAULTS.retryBaseMs;
    const binary = def.responseKind === 'binary';
    // 204 No Content routes (e.g. `POST /me/logout`): nothing to parse, nothing to validate.
    const empty = def.responseKind === 'empty';

    let lastError: AkpError | undefined;
    for (let n = 1; n <= retries + 1; n++) {
      // Headers are rebuilt per attempt so a refreshed token is picked up.
      const headers = await headersFor(def, args.headers);
      const body = encodeBody(def, args.body, headers);
      let res: Response;
      try {
        res = await attempt(def, target, headers, body, timeoutMs, args.signal);
      } catch (err) {
        lastError = err as AkpError;
        const abortedByCaller = args.signal?.aborted === true;
        if (abortedByCaller || n > retries) break;
        await sleep(backoffMs(retryBase, n));
        continue;
      }

      const payload = await readBody(res, binary);
      if (!res.ok) {
        lastError = AkpError.fromResponse(
          res.status,
          payload,
          `${def.method} ${def.path} → ${res.status}`,
        );
        if (n <= retries && isRetryableStatus(res.status)) {
          await sleep(backoffMs(retryBase, n));
          continue;
        }
        break;
      }

      if (binary) return payload;
      if (empty) return undefined;
      if (!validate) return payload;
      const parsed = def.response.safeParse(payload);
      if (parsed.success) return parsed.data;
      throw new AkpError(
        ErrorCodes.contract_violation,
        `Response of ${def.method} ${def.path} does not match its contract`,
        { status: res.status, details: { issues: parsed.error.issues } },
      );
    }

    const error = lastError ?? new AkpError(ErrorCodes.internal, 'Request failed', { status: 0 });
    options.onError?.(error, { route: key, attempt: retries + 1 });
    throw error;
  }

  const client = {
    request,
    url: (key: RouteKey, args?: { params?: Record<string, string>; query?: object }) =>
      url(routes[key], args),
  } as Record<string, unknown>;
  for (const key of Object.keys(routes) as RouteKey[]) {
    client[key] = (args?: unknown) => request(key, args);
  }
  return client as unknown as ApiClient;
}
